import { TranslateService } from '@ngx-translate/core';
import { ISortDescriptor } from '@nimbus-library/features/shared/models/interfaces';
import { IMap } from '@nimbus-library/features/shared/models/interfaces';
import { IOverlayFieldMeta, OverlayFieldType } from '@nimbus-library/features/shared/models/overlay-entity/i-overlay-field-meta.model';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IContactNoteModel } from '@nimbus-library/features/shared/models/contacts/i-contact-note.model';
import { TranslationKeysMapper } from '@nimbus-library/features/localization/models/translation-keys.model';

export interface IContactSortable {
  displayName: string;
  jobTitle: string;
  contactDetails?: string;
  noteDisplayed?: string;
}

export interface IContactPresenceSortable {
  contactPresenceCode: ContactPresenceCode;
}

export interface IContact extends IContactSortable {
  id: string;
  teamId?: string;
  initials: string;
  sourceType?: ContactSourceType;
  mail: string;
  mailAddresses?: string[];
  mailUrl?: string;
  imAddresses?: string[];
  addressBookId?: string;
  userPrincipalName?: string;
  businessPhones: string[];
  mobilePhone?: string;
  mobilePhones?: string[];
  homePhones: string[];
  department?: string;
  callInformation?: ICallInformation[];
  isPhoneOnly?: boolean;
  isFavourite?: boolean;
  isMe?: boolean;
  isService?: boolean;
  isNimbusUnifyService?: boolean;
  chatDisabled?: boolean;
  contactPresenceCode?: ContactPresenceCode;
  presence?: IContactPresence;
  hasTeams?: boolean;
  customContactIsNimbusService?: boolean;
  notes?: IContactNoteModel[];
  note?: IContactNoteModel;
  metaInfo?: IContactMetaInfo;
  ouParams?: IMap<string[]>;
  iconPath?: string;
  groupId?: string;
}

export interface IPinnedContact extends IContact {
  pinType?: IContactPinType;
}

export interface IContactGroup {
  id: string;
  default: boolean;
  name: string;
  order: number;
  visible: boolean;
  expanded: boolean;
  contacts: IContactData[];
  contactsList: IContact[];
}

export interface IContactData {
  id: string;
  tag: string;
}

export interface IContactGroupModel {
  group: IContactGroup;
  orderNumber: number;
}
export interface IContactAction {
  contact: IContact;
  type: IContactActionType;
  selectedTarget?: string;
  translateService?: TranslateService;
}

export interface ICallInformation {
  type?: string;
  phoneNumber: string;
  link: string;
}

export interface IContactSearchGridStateRequest {
  skip: number;
  take: number;
  takeBatch: number;
  search: string;
  sort: ISortDescriptor<IContactSortable>[];
  presenceSort?: ISortDescriptor<IContactPresenceSortable>;
  local: boolean;
}

export interface IContactPresence {
  availability: string;
  activity: string;
  id: string;
}

export interface IContactPhoto {
  data: string;
  id: string;
}

export enum ContactSourceType {
  ActiveDirectory = 'ActiveDirectory',
  ExchangeContact = 'ExchangeContact',
  NimbusTeam = 'NimbusTeam',
  TmTeam = 'TmTeam',
  External = 'External',
  Custom = 'Custom',
}

export const ContactSourceTypeTranslateMap: TranslationKeysMapper<ContactSourceType> = {
  ActiveDirectory: TranslationKeys.GeneralContactSourceTypeActiveDirectory,
  ExchangeContact: TranslationKeys.GeneralContactSourceTypeExchangeContact,
  NimbusTeam: TranslationKeys.GeneralContactSourceTypeNimbusTeam,
  TmTeam: TranslationKeys.GeneralContactSourceTypeTmTeam,
  External: TranslationKeys.GeneralContactSourceTypeExternal,
  Custom: TranslationKeys.GeneralContactSourceTypeCustom,
};

export type ContactSourceTypeLabel = { [key in ContactSourceType]?: string };

// noinspection JSUnusedGlobalSymbols
export enum ContactPresenceCode {
  Available = 0,
  Busy = 1,
  InACall = 2,
  InAMeeting = 3,
  DND = 4,
  Presenting = 5,
  Focusing = 6,
  Away = 7,
  BRB = 8,
  OffWork = 9,
  Offline = 10,
  Unknown = 11,
  Blocked = 12,
  OutOfOffice = 13,
  DirectSelectable = 14,
  InTimeSelectable = 15,
  NoOneAvailable = 16,
}

// ToDo, ange: better to split this list to:
//  - actions of Contacts functionality
//  - actions for Call control functionality
export enum IContactActionType {
  Mail = 'Mail',
  IM = 'IM',
  Call = 'Call',
  SafeTransfer = 'SafeTransfer',
  BlindTransfer = 'BlindTransfer',
  Voicemail = 'Voicemail',
  Calendar = 'Calendar',
  Resume = 'Resume',
  Hold = 'Hold',
  Park = 'Park',
  Unpark = 'Unpark',
  Reply = 'Reply',
  Delete = 'Delete',
  Edit = 'Edit',
  Terminate = 'Terminate',
  AddFavourite = 'AddFavourite',
  RemoveFavourite = 'RemoveFavourite',
  MakeConsultativeCall = 'MakeConsultativeCall',
  TerminateConsultativeCall = 'TerminateConsultativeCall',
  TerminateConsultativeCallAndResume = 'TerminateConsultativeCallAndResume',
  TerminateCustomerCall = 'TerminateCustomerCall',
  Merge = 'Merge',
  Swap = 'Swap',
  Accept = 'Accept',
  Decline = 'Decline',
}

export const callOperations = [
  IContactActionType.SafeTransfer,
  IContactActionType.BlindTransfer,
  IContactActionType.Resume,
  IContactActionType.Hold,
  IContactActionType.Terminate,
  IContactActionType.MakeConsultativeCall,
  IContactActionType.TerminateConsultativeCall,
  IContactActionType.TerminateCustomerCall,
  IContactActionType.Merge,
  IContactActionType.Swap,
];

export enum IContactPhoneType {
  Home = 'Home',
  Business = 'Business',
  Mobile = 'Mobile',
}

export enum IContactPinType {
  Service = 'Service',
  Manager = 'Manager',
  Peers = 'Peers',
  Reports = 'Reports',
}

export const ContactPinTypeTranslateMap: TranslationKeysMapper<IContactPinType> = {
  Service: TranslationKeys.GeneralContactPinTypeService,
  Manager: TranslationKeys.GeneralContactPinTypeManager,
  Peers: TranslationKeys.GeneralContactPinTypePeers,
  Reports: TranslationKeys.GeneralContactPinTypeReports,
};

export interface IContactMetaInfo {
  firstName: string;
  lastName: string;
  displayName: string;
  imAddress: string;
  emailAddress: string;
  businessPhone: string;
  mobilePhone: string;
  homePhone: string;
  userPrincipalName: string;
  city: string;
  country: string;
  state: string;
  company: string;
  department: string;
  initials: string;
  jobTitle: string;
  postalCode: string;
  street: string;
  [key: string]: string;
}

export const contactMetaFieldConfiguration: Record<keyof IContactMetaInfo, IOverlayFieldMeta> = {
  firstName: { translation: TranslationKeys.GeneralFirstName, type: OverlayFieldType.Text },
  lastName: { translation: TranslationKeys.GeneralLastName, type: OverlayFieldType.Text },
  displayName: { translation: TranslationKeys.GeneralDisplayName, type: OverlayFieldType.Text },
  imAddress: { translation: TranslationKeys.GeneralImAddress, type: OverlayFieldType.Text },
  emailAddress: { translation: TranslationKeys.GeneralMailAddress, type: OverlayFieldType.Text },
  businessPhone: { translation: TranslationKeys.GeneralBusinessPhone, type: OverlayFieldType.Text },
  mobilePhone: { translation: TranslationKeys.GeneralMobilePhone, type: OverlayFieldType.Text },
  homePhone: { translation: TranslationKeys.GeneralHomePhone, type: OverlayFieldType.Text },
  userPrincipalName: { translation: TranslationKeys.GeneralUpn, type: OverlayFieldType.Text },
  city: { translation: TranslationKeys.GeneralCity, type: OverlayFieldType.Text },
  country: { translation: TranslationKeys.GeneralCountry, type: OverlayFieldType.Text },
  state: { translation: TranslationKeys.GeneralState, type: OverlayFieldType.Text },
  company: { translation: TranslationKeys.GeneralCompany, type: OverlayFieldType.Text },
  department: { translation: TranslationKeys.GeneralDepartment, type: OverlayFieldType.Text },
  initials: { translation: TranslationKeys.GeneralInitials, type: OverlayFieldType.Text },
  jobTitle: { translation: TranslationKeys.GeneralJobtitle, type: OverlayFieldType.Text },
  postalCode: { translation: TranslationKeys.GeneralPostalCode, type: OverlayFieldType.Text },
  street: { translation: TranslationKeys.GeneralStreet, type: OverlayFieldType.Text },
};
